// Pure algorithms for the Location Diagnosis (Diagnosticador de Locales) tool.
// Pre-firma: evaluates a retail unit BEFORE the tenant signs the lease.
// Two separate engines depending on the user role:
//   - computeDiagnosis()       → tenant (retailer) viability
//   - computeOwnerDiagnosis()  → landlord / agency tenant-risk score

export type Semaforo = "Verde" | "Amarillo" | "Rojo";
export type RetailRole = "inquilino" | "propietario";

export const SECTORS = ["Moda", "Hostelería", "Cosmética", "Servicios", "Alimentación"] as const;
export type Sector = typeof SECTORS[number];

// ── Sector rule families (used by the tenant engine only) ──
// Keys are internal identifiers; mapping from the UI detailed sector id
// (tienda_ropa, restaurante, …) lives in SECTOR_RULES_BY_DETAILED.
export type SectorRuleKey =
  | "Textil"
  | "Hostelería"
  | "Alimentación"
  | "Servicios"
  | "Salud"
  | "TicketAlto"
  | "Otros";

interface SectorRule {
  limiteEsfuerzoMax: number; // max acceptable rent-effort ratio (0-1)
  margenBruto: number;       // gross margin (0-1)
}

export const SECTOR_RULES: Record<SectorRuleKey, SectorRule> = {
  Textil:       { limiteEsfuerzoMax: 0.12, margenBruto: 0.55 },
  Hostelería:   { limiteEsfuerzoMax: 0.10, margenBruto: 0.65 },
  Alimentación: { limiteEsfuerzoMax: 0.06, margenBruto: 0.30 },
  Servicios:    { limiteEsfuerzoMax: 0.18, margenBruto: 0.80 },
  Salud:        { limiteEsfuerzoMax: 0.10, margenBruto: 0.45 },
  TicketAlto:   { limiteEsfuerzoMax: 0.15, margenBruto: 0.50 },
  Otros:        { limiteEsfuerzoMax: 0.10, margenBruto: 0.50 },
};

// Map UI-level detailed sector ids → rule family.
export const SECTOR_RULES_BY_DETAILED: Record<string, SectorRuleKey> = {
  tienda_ropa: "Textil",
  zapateria: "Textil",
  joyeria: "TicketAlto",
  restaurante: "Hostelería",
  bar: "Hostelería",
  cafeteria: "Hostelería",
  panaderia: "Alimentación",
  supermercado: "Alimentación",
  alimentacion: "Alimentación",
  peluqueria: "Servicios",
  estetica: "Servicios",
  farmacia: "Salud",
  parafarmacia: "Salud",
  muebles: "TicketAlto",
  otro: "Otros",
};

export function getSectorRule(detailedSectorId: string | undefined | null): SectorRule {
  const key = (detailedSectorId && SECTOR_RULES_BY_DETAILED[detailedSectorId]) || "Otros";
  return SECTOR_RULES[key];
}

// Per detailed-sector loaded employee cost (€/mo, "coste empresa").
// Used by the tenant break-even engine (overhead helper) and by the UI breakdown.
export const SECTOR_EMPLOYEE_COST: Record<string, number> = {
  tienda_ropa: 1850,
  zapateria: 1850,
  joyeria: 2000,
  restaurante: 1950,
  bar: 1950,
  cafeteria: 1950,
  panaderia: 1780,
  supermercado: 1780,
  alimentacion: 1780,
  peluqueria: 1750,
  estetica: 1750,
  farmacia: 2300,
  parafarmacia: 2300,
  muebles: 2000,
  otro: 1850,
};

export const FIXED_OVERHEAD_MONTHLY = 400; // suministros + gestoría
export const OPTIMAL_EFFORT_RATIO = 0.08;

export function getEmployeeCost(detailedSectorId: string | undefined | null): number {
  if (!detailedSectorId) return 1850;
  return SECTOR_EMPLOYEE_COST[detailedSectorId] ?? 1850;
}


// ── Tenant-side legal form / experience (consumed by owner engine) ──
export type TenantLegalFormId = "autonomo" | "sl" | "sa" | "extranjera";
export type TenantExperienceId = "primera" | "1_2" | "cadena";

export interface DiagnosisInput {
  role: RetailRole;
  zipCode: string;
  requestedRent: number;      // €/mes
  sqm: number;
  sector: Sector;             // broad sector (used for zone viability)
  detailedSectorId?: string | undefined;  // UI-level id (used to pick SECTOR_RULES)
  averageTicket: number;      // € por venta (tenant only)
  diasApertura: number;       // días/mes
  empleadosEstimados?: number; // tenant only; default 1
}

export interface OwnerDiagnosisInput {
  zipCode: string;
  requestedRent: number;
  sqm: number;
  sector: Sector;
  detailedSectorId?: string | undefined;
  tenantLegalForm: TenantLegalFormId | null;
  tenantExperience: TenantExperienceId | null;
}

export interface ZoneRef {
  zip_code: string;
  neighborhood_name: string;
  avg_rent_sqm: number;
  sector_viability: Record<string, boolean>;
}

export interface DiagnosisResult {
  semaforo: Semaforo;
  precioM2Usuario: number;
  precioM2Zona: number;
  ratioVsZona: number;
  // Tenant-only metrics (0 when not applicable)
  unidadesLimite: number;
  unidadesOptimas: number;
  facturacionLimite: number;   // = facturación necesaria break-even
  facturacionOptima: number;   // = facturación cómoda (esfuerzo en óptimo)
  tasaEsfuerzoReal: number;    // rent / facturación necesaria (0-1)
  limiteEsfuerzoSector: number;// from sector rules (0-1)
  puntoEquilibrio: number;     // gastos fijos mínimos
  ventasAlertaAlta: boolean;
  sectorViableEnZona: boolean;
  zoneFound: boolean;
  neighborhoodName: string;
  // Cost breakdown (tenant engine) — exposed for UI breakdown + simulator
  margenBruto: number;          // 0-1
  costePersonalUnitario: number;// €/empleado/mes
  empleadosEstimados: number;
  costesLaborales: number;      // empleados * costePersonalUnitario
  overheadFijo: number;         // suministros + gestoría (FIXED_OVERHEAD_MONTHLY)
  // Owner-only metrics
  ownerScore?: number;          // 0-100 (only present on owner engine)
}

const DEFAULT_RENT_SQM = 25;
const HIGH_VOLUME_ALERT_UNITS = 60;   // u/día

// ────────────────────────────────────────────────────────────────────────────
// TENANT ENGINE
// ────────────────────────────────────────────────────────────────────────────
export function computeDiagnosis(input: DiagnosisInput, zone: ZoneRef | null): DiagnosisResult {
  const precioM2Usuario = input.sqm > 0 ? input.requestedRent / input.sqm : 0;
  const precioM2Zona = zone?.avg_rent_sqm ?? DEFAULT_RENT_SQM;
  const ratio = precioM2Zona > 0 ? precioM2Usuario / precioM2Zona : 0;

  const rule = getSectorRule(input.detailedSectorId);
  const ticket = input.averageTicket > 0 ? input.averageTicket : 1;
  const dias = input.diasApertura > 0 ? input.diasApertura : 26;
  const empleados = Math.max(1, input.empleadosEstimados ?? 1);
  const costePersonalUnitario = getEmployeeCost(input.detailedSectorId);
  const costesLaborales = empleados * costePersonalUnitario;

  // Break-even + needed revenue
  const puntoEquilibrio = input.requestedRent + costesLaborales + FIXED_OVERHEAD_MONTHLY;
  const facturacionNecesaria = puntoEquilibrio / rule.margenBruto;
  const facturacionOptima = input.requestedRent / OPTIMAL_EFFORT_RATIO;
  const tasaEsfuerzoReal = facturacionNecesaria > 0 ? input.requestedRent / facturacionNecesaria : 1;


  const unidadesLimite = Math.ceil(facturacionNecesaria / (ticket * dias));
  const unidadesOptimas = Math.ceil(facturacionOptima / (ticket * dias));

  // Semaforo rules (tenant)
  let semaforo: Semaforo;
  const overEffort = tasaEsfuerzoReal > rule.limiteEsfuerzoMax;
  const nearLimit = !overEffort && (rule.limiteEsfuerzoMax - tasaEsfuerzoReal) < 0.02;

  if (overEffort || ratio > 1.2) {
    semaforo = "Rojo";
  } else if (nearLimit || (ratio >= 1.05 && ratio <= 1.2) || unidadesLimite > HIGH_VOLUME_ALERT_UNITS) {
    semaforo = "Amarillo";
  } else {
    semaforo = "Verde";
  }

  const ventasAlertaAlta = unidadesLimite > HIGH_VOLUME_ALERT_UNITS;
  const sectorViableEnZona = zone ? zone.sector_viability?.[input.sector] !== false : true;
  if (!sectorViableEnZona && semaforo === "Verde") semaforo = "Amarillo";

  return {
    semaforo,
    precioM2Usuario,
    precioM2Zona,
    ratioVsZona: ratio,
    unidadesLimite,
    unidadesOptimas,
    facturacionLimite: facturacionNecesaria,
    facturacionOptima,
    tasaEsfuerzoReal,
    limiteEsfuerzoSector: rule.limiteEsfuerzoMax,
    puntoEquilibrio,
    ventasAlertaAlta,
    sectorViableEnZona,
    zoneFound: !!zone,
    neighborhoodName: zone?.neighborhood_name ?? "",
    margenBruto: rule.margenBruto,
    costePersonalUnitario,
    empleadosEstimados: empleados,
    costesLaborales,
    overheadFijo: FIXED_OVERHEAD_MONTHLY,
  };

}

// ────────────────────────────────────────────────────────────────────────────
// OWNER ENGINE — tenant default-risk score (0-100)
// ────────────────────────────────────────────────────────────────────────────
export function computeOwnerDiagnosis(input: OwnerDiagnosisInput, zone: ZoneRef | null): DiagnosisResult {
  const precioM2Usuario = input.sqm > 0 ? input.requestedRent / input.sqm : 0;
  const precioM2Zona = zone?.avg_rent_sqm ?? DEFAULT_RENT_SQM;
  const ratio = precioM2Zona > 0 ? precioM2Usuario / precioM2Zona : 0;

  let score = 100;

  // Penalty: rent vs zone
  if (ratio > 1.3) score -= 40;
  else if (ratio > 1.1) score -= 20;

  // Penalty/bonus: experience
  switch (input.tenantExperience) {
    case "primera": score -= 30; break;
    case "1_2":     score -= 10; break;
    case "cadena":  score += 10; break;
  }

  // Penalty/bonus: legal form
  switch (input.tenantLegalForm) {
    case "autonomo":   score -= 20; break;
    case "sl":         score += 0;  break;
    case "sa":         score += 10; break;
    case "extranjera": score += 10; break;
  }

  score = Math.max(0, Math.min(100, score));

  let semaforo: Semaforo;
  if (score >= 70) semaforo = "Verde";
  else if (score >= 40) semaforo = "Amarillo";
  else semaforo = "Rojo";

  const sectorViableEnZona = zone ? zone.sector_viability?.[input.sector] !== false : true;

  return {
    semaforo,
    precioM2Usuario,
    precioM2Zona,
    ratioVsZona: ratio,
    unidadesLimite: 0,
    unidadesOptimas: 0,
    facturacionLimite: 0,
    facturacionOptima: 0,
    tasaEsfuerzoReal: 0,
    limiteEsfuerzoSector: 0,
    puntoEquilibrio: 0,
    ventasAlertaAlta: false,
    sectorViableEnZona,
    zoneFound: !!zone,
    neighborhoodName: zone?.neighborhood_name ?? "",
    margenBruto: getSectorRule(input.detailedSectorId).margenBruto,
    costePersonalUnitario: getEmployeeCost(input.detailedSectorId),
    empleadosEstimados: 0,
    costesLaborales: 0,
    overheadFijo: FIXED_OVERHEAD_MONTHLY,
    ownerScore: score,
  };

}

export const semaforoColor = (s: Semaforo) =>
  s === "Verde" ? "#16a34a" : s === "Amarillo" ? "#FFE143" : "#dc2626";

export const PENDING_DIAGNOSIS_KEY = "pendingLocationDiagnosis";
export const GUEST_DIAGNOSIS_HISTORY_KEY = "guestLocationHistory";
const GUEST_HISTORY_MAX = 10;

export interface PendingDiagnosis {
  input: DiagnosisInput;
  result: DiagnosisResult;
  sessionId: string;
  savedAt: string;
}

/** Append a guest diagnosis to a capped history so multiple pre-login
 *  searches survive registration (and all get upserted to saved_locations). */
export function pushGuestDiagnosis(entry: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  try {
    const raw = localStorage.getItem(GUEST_DIAGNOSIS_HISTORY_KEY);
    const list: Record<string, unknown>[] = raw ? JSON.parse(raw) : [];
    list.push(entry);
    const trimmed = list.slice(-GUEST_HISTORY_MAX);
    localStorage.setItem(GUEST_DIAGNOSIS_HISTORY_KEY, JSON.stringify(trimmed));
  } catch {/* ignore quota / private mode */}
}
